import { AlertTriangle, ArrowLeft, FileText, Flag, Layers } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { TypeChip } from "@/components/ui/TypeChip";
import { useActiveOverheadCategories } from "@/hooks/use-overhead-categories";
import { type TimeEntry, useCreateTimeEntry, useUpdateTimeEntry } from "@/hooks/use-time-entries";
import type { WorkOrder } from "@/hooks/use-work-orders";
import { CATEGORIES } from "@/lib/usg-constants";
import { timeEntryWriteSchema } from "@/schemas/time-entries";
import { CategoryDropdown } from "./CategoryDropdown";
import { HoursStepper } from "./HoursStepper";
import { WorkOrderCombobox } from "./WorkOrderCombobox";

interface TimeEntryFormProps {
  editing: TimeEntry | null;
  onCancel: () => void;
  onSaved: (addAnother: boolean) => void;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function TimeEntryForm({ editing, onCancel, onSaved }: TimeEntryFormProps) {
  const isEdit = !!editing;
  const isFlagged = editing?.status === "flagged";

  const { data: activeOverheadCategories = [] } = useActiveOverheadCategories();

  const [type, setType] = useState<"billable" | "overhead">(
    (editing?.type as "billable" | "overhead") ?? "billable",
  );
  const [date, setDate] = useState(editing?.entryDate ?? todayISO());
  const [wo, setWO] = useState<WorkOrder | null>(null);
  const [category, setCategory] = useState(editing?.category ?? "");
  const [subtask, setSubtask] = useState(editing?.subtask ?? "");
  const [overheadCode, setOverheadCode] = useState("");
  const [overheadCategoryId, setOverheadCategoryId] = useState<string | null>(null);
  const [overheadSub, setOverheadSub] = useState(editing?.overheadSub ?? "");
  const [overheadSubcategoryId, setOverheadSubcategoryId] = useState<string | null>(
    editing?.overheadSubcategoryId ?? null,
  );
  const [hours, setHours] = useState(editing?.hours ?? 0.5);
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSwitchWarn, setShowSwitchWarn] = useState(false);

  const overheadInitialized = useRef(false);
  useEffect(() => {
    if (
      overheadInitialized.current ||
      !editing?.overheadCode ||
      activeOverheadCategories.length === 0
    )
      return;
    overheadInitialized.current = true;
    const cat = activeOverheadCategories.find((c) => c.code === editing.overheadCode);
    if (cat) {
      setOverheadCode(`${cat.code} · ${cat.label}`);
      setOverheadCategoryId(cat.id);
      if (editing.overheadSub) {
        const sub = cat.subcategories.find((s) => s.name === editing.overheadSub);
        setOverheadSubcategoryId(sub?.id ?? null);
      }
    } else {
      setOverheadCode(editing.overheadCode);
      setOverheadCategoryId(editing.overheadCategoryId ?? null);
    }
  }, [editing, activeOverheadCategories]);

  const createEntry = useCreateTimeEntry();
  const updateEntry = useUpdateTimeEntry();
  const isPending = createEntry.isPending || updateEntry.isPending;

  const jobType = wo?.jobType ?? editing?.category ?? "";

  const categoryOptions = useMemo(() => {
    if (type !== "billable" || !jobType) return [];
    const cat = CATEGORIES[jobType];
    return cat ? [cat.label] : [];
  }, [type, jobType]);

  const subtaskOptions = useMemo(() => {
    if (!category || !jobType) return [];
    return CATEGORIES[jobType]?.subs ?? [];
  }, [category, jobType]);

  const overheadOptions = useMemo(
    () => activeOverheadCategories.map((c) => `${c.code} · ${c.label}`),
    [activeOverheadCategories],
  );

  const selectedOverheadCategory = useMemo(() => {
    if (!overheadCode) return null;
    const code = overheadCode.split(" · ")[0];
    return activeOverheadCategories.find((c) => c.code === code) ?? null;
  }, [overheadCode, activeOverheadCategories]);

  const overheadSubOptions = useMemo(
    () => selectedOverheadCategory?.subcategories.map((s) => s.name) ?? [],
    [selectedOverheadCategory],
  );

  function switchType(next: "billable" | "overhead") {
    if (next === type) return;
    const hasSelections = wo ?? overheadCode ?? category ?? subtask;
    if (hasSelections) {
      setShowSwitchWarn(true);
      setTimeout(() => setShowSwitchWarn(false), 2500);
    }
    setType(next);
    setWO(null);
    setCategory("");
    setSubtask("");
    setOverheadCode("");
    setOverheadCategoryId(null);
    setOverheadSub("");
    setOverheadSubcategoryId(null);
  }

  function validate() {
    const code = overheadCode.split(" · ")[0];
    const effectiveCategory = wo ? wo.jobType : (editing?.category ?? category);
    const effectiveWoNumber = wo?.woNumber ?? editing?.woNumber ?? null;

    const result = timeEntryWriteSchema.safeParse({
      entryDate: date,
      type,
      woNumber: type === "billable" ? effectiveWoNumber : null,
      category: type === "billable" ? effectiveCategory : null,
      subtask: type === "billable" ? subtask : null,
      overheadCode: type === "overhead" ? code : null,
      overheadSub: type === "overhead" ? overheadSub : null,
      hours,
      notes: notes || null,
    });

    const e: Record<string, string> = {};
    if (!result.success) {
      for (const issue of result.error.issues) {
        const path = String(issue.path[0] ?? "");
        const key =
          path === "entryDate"
            ? "date"
            : path === "woNumber"
              ? "wo"
              : path === "overheadCode"
                ? "overhead"
                : path;
        if (!e[key]) e[key] = issue.message;
      }
    }

    if (type === "overhead" && overheadSubOptions.length > 0 && !overheadSub) {
      e.overheadSub = "Please select a subcategory";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(addAnother: boolean) {
    if (!validate()) return;

    const code = overheadCode.split(" · ")[0];
    const effectiveCategory = wo ? wo.jobType : (editing?.category ?? category);

    if (isEdit && editing) {
      await updateEntry.mutateAsync({
        id: editing.id,
        data: {
          entryDate: date,
          type,
          woNumber: type === "billable" ? (wo?.woNumber ?? editing.woNumber) : null,
          category: type === "billable" ? effectiveCategory : null,
          subtask: type === "billable" ? subtask : null,
          overheadCode: type === "overhead" ? code : null,
          overheadSub: type === "overhead" ? overheadSub : null,
          overheadCategoryId: type === "overhead" ? overheadCategoryId : null,
          overheadSubcategoryId: type === "overhead" ? overheadSubcategoryId : null,
          hours,
          notes: notes || null,
          status: isFlagged ? "submitted" : editing.status,
        },
      });
    } else {
      await createEntry.mutateAsync({
        entryDate: date,
        type,
        woNumber: type === "billable" ? wo?.woNumber : null,
        category: type === "billable" ? (wo?.jobType ?? "") : null,
        subtask: type === "billable" ? subtask : null,
        overheadCode: type === "overhead" ? code : null,
        overheadSub: type === "overhead" ? overheadSub : null,
        overheadCategoryId: type === "overhead" ? overheadCategoryId : null,
        overheadSubcategoryId: type === "overhead" ? overheadSubcategoryId : null,
        hours,
        notes: notes || null,
      });
    }

    onSaved(addAnother);
  }

  const saveBtnLabel = isPending
    ? "Saving…"
    : isFlagged
      ? "Save & Resubmit"
      : isEdit
        ? "Save Changes"
        : "Save Entry";

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={onCancel}
        style={{ marginBottom: 12, paddingLeft: 0 }}
      >
        <ArrowLeft size={14} /> Back to Dashboard
      </button>

      <div style={{ marginBottom: 22 }}>
        <h1 className="page-header__title" style={{ fontSize: 26 }}>
          {isFlagged ? "Resolve Flagged Entry" : isEdit ? "Edit Time Entry" : "Log Time"}
        </h1>
        <p className="page-header__sub" style={{ marginTop: 4 }}>
          {isFlagged
            ? "Correct the issue below and save to resubmit for review."
            : "Smart defaults are filled in where possible. Press Tab to move through the form."}
        </p>
      </div>

      {isFlagged && editing?.flagManager && (
        <div className="callout callout--warn" style={{ marginBottom: 20 }}>
          <Flag size={18} className="callout__icon" />
          <div>
            <strong className="callout__title">Flagged by {editing.flagManager}</strong>
            <div className="callout__body">{editing.flagNote}</div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card__body">
          <div className="form-grid">
            {/* Entry type toggle */}
            <div className="form-field">
              <div className="form-field__label">Entry Type</div>
              <div className="segmented" role="tablist">
                <button
                  type="button"
                  className={`segmented__option${type === "billable" ? " is-active is-active--green" : ""}`}
                  onClick={() => switchType("billable")}
                >
                  <FileText size={14} /> Billable WO
                </button>
                <button
                  type="button"
                  className={`segmented__option${type === "overhead" ? " is-active is-active--gray" : ""}`}
                  onClick={() => switchType("overhead")}
                >
                  <Layers size={14} /> Overhead
                </button>
              </div>
              {showSwitchWarn && (
                <div className="form-field__hint" style={{ color: "var(--amber)" }}>
                  Switching entry type cleared your selections.
                </div>
              )}
            </div>

            {/* Date */}
            <div className="form-field">
              <label className="form-field__label" htmlFor="entry-date">
                <span>
                  Date <span className="required">*</span>
                </span>
              </label>
              <div style={{ position: "relative", maxWidth: 280 }}>
                <input
                  id="entry-date"
                  type="date"
                  className={`form-input${errors.date ? " is-error" : ""}`}
                  value={date}
                  max={todayISO()}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              {errors.date && (
                <div className="form-field__error">
                  <AlertTriangle size={12} /> {errors.date}
                </div>
              )}
            </div>

            {/* Billable path */}
            {type === "billable" && (
              <>
                <div className="form-field">
                  <div className="form-field__label">
                    <span>
                      Work Order <span className="required">*</span>
                    </span>
                    {editing?.woNumber && !wo && (
                      <span className="form-field__hint">Current: {editing.woNumber}</span>
                    )}
                  </div>
                  <WorkOrderCombobox
                    value={wo}
                    onChange={(v) => {
                      setWO(v);
                      if (!isEdit) {
                        setCategory("");
                        setSubtask("");
                      }
                    }}
                  />
                  {errors.wo && (
                    <div className="form-field__error">
                      <AlertTriangle size={12} /> {errors.wo}
                    </div>
                  )}
                </div>

                <div className="form-field">
                  <div className="form-field__label">
                    <span>
                      Activity Category <span className="required">*</span>
                    </span>
                  </div>
                  <CategoryDropdown
                    options={categoryOptions}
                    value={category}
                    onChange={(v) => {
                      setCategory(v);
                      setSubtask("");
                    }}
                    placeholder={jobType ? `Select category for ${jobType}…` : "Locked"}
                    locked={!jobType}
                    lockedMessage="Select a Work Order first"
                    hasError={!!errors.category}
                  />
                  {errors.category && (
                    <div className="form-field__error">
                      <AlertTriangle size={12} /> {errors.category}
                    </div>
                  )}
                </div>

                <div className="form-field">
                  <div className="form-field__label">
                    <span>
                      Subtask <span className="required">*</span>
                    </span>
                  </div>
                  <CategoryDropdown
                    options={subtaskOptions}
                    value={subtask}
                    onChange={setSubtask}
                    placeholder="Select subtask…"
                    locked={!category}
                    lockedMessage="Select a category first"
                    hasError={!!errors.subtask}
                  />
                  {errors.subtask && (
                    <div className="form-field__error">
                      <AlertTriangle size={12} /> {errors.subtask}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Overhead path */}
            {type === "overhead" && (
              <>
                <div className="form-field">
                  <div className="form-field__label">
                    <span>
                      Overhead Category <span className="required">*</span>
                    </span>
                  </div>
                  <CategoryDropdown
                    options={overheadOptions}
                    value={overheadCode}
                    onChange={(v) => {
                      setOverheadCode(v);
                      setOverheadSub("");
                      setOverheadSubcategoryId(null);
                      const code = v.split(" · ")[0];
                      const cat = activeOverheadCategories.find((c) => c.code === code);
                      setOverheadCategoryId(cat?.id ?? null);
                    }}
                    placeholder="Select overhead code…"
                    hasError={!!errors.overhead}
                  />
                  {errors.overhead && (
                    <div className="form-field__error">
                      <AlertTriangle size={12} /> {errors.overhead}
                    </div>
                  )}
                </div>

                {overheadSubOptions.length > 0 && (
                  <div className="form-field">
                    <div className="form-field__label">
                      <span>
                        Subcategory <span className="required">*</span>
                      </span>
                    </div>
                    <CategoryDropdown
                      options={overheadSubOptions}
                      value={overheadSub}
                      onChange={(v) => {
                        setOverheadSub(v);
                        const sub = selectedOverheadCategory?.subcategories.find(
                          (s) => s.name === v,
                        );
                        setOverheadSubcategoryId(sub?.id ?? null);
                      }}
                      placeholder="Select subcategory…"
                      hasError={!!errors.overheadSub}
                    />
                    {errors.overheadSub && (
                      <div className="form-field__error">
                        <AlertTriangle size={12} /> {errors.overheadSub}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Hours */}
            <div className="form-field">
              <div className="form-field__label">
                <span>
                  Hours <span className="required">*</span>
                </span>
                <span className="form-field__hint">30-minute increments</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <HoursStepper value={hours} onChange={setHours} hasError={!!errors.hours} />
                <TypeChip type={type} />
              </div>
              {errors.hours && (
                <div className="form-field__error">
                  <AlertTriangle size={12} /> {errors.hours}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="form-field">
              <label className="form-field__label" htmlFor="entry-notes">
                <span>Notes</span>
                <span className="form-field__hint">Optional · {notes.length}/500</span>
              </label>
              <textarea
                id="entry-notes"
                className={`form-input form-textarea${errors.notes ? " is-error" : ""}`}
                placeholder="Add context, issues, or details…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
              />
              {errors.notes && (
                <div className="form-field__error">
                  <AlertTriangle size={12} /> {errors.notes}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card__footer">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </button>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {!isEdit && (
              <button
                type="button"
                className="btn-link"
                onClick={() => submit(true)}
                disabled={isPending}
              >
                + Save &amp; Add Another
              </button>
            )}
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => submit(false)}
              disabled={isPending}
            >
              {saveBtnLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
